import React, { useState, useEffect } from 'react';
import {
  IonContent,
  IonPage,
  IonAvatar,
  IonItem,
  IonLabel,
  IonList,
  IonIcon,
  IonButton,
  IonSelect,
  IonSelectOption,
} from '@ionic/react';

import {
  personCircle,
  cardOutline,
  logOutOutline,
  languageOutline,
} from 'ionicons/icons';
import dayjs from 'dayjs';
import 'dayjs/locale/fr';
import { useHistory } from 'react-router-dom';
import { auth, db } from '../firebaseConfig';
import { doc, getDoc } from 'firebase/firestore';
import { signOut } from 'firebase/auth';
import { useTranslation } from 'react-i18next';
import NavBar from './NavBar';

const Dashboard: React.FC = () => {
  const { t, i18n } = useTranslation();
  const history = useHistory();
  const [userData, setUserData] = useState<any>(null);
  const [showLanguageSelect, setShowLanguageSelect] = useState(false);

  useEffect(() => {
    const fetchUserData = async () => {
      const user = auth.currentUser;
      if (user) {
        const userDoc = await getDoc(doc(db, 'users', user.uid));
        if (userDoc.exists()) {
          const data = userDoc.data();
          setUserData(data);
        }
      }
    };
    fetchUserData();
  }, []);

  const handleLogout = async () => {
    try {
      await signOut(auth);
      history.push('/login');
    } catch (error) {
      console.error('Error signing out:', error);
    }
  };

  const formatDate = (timestamp: any) => {
    if (!timestamp || !timestamp.seconds) return 'Date invalide';
    
    dayjs.locale('fr');
    // Convert Firestore Timestamp to milliseconds
    const date = new Date(timestamp.seconds * 1000);
    return dayjs(date).format('DD/MM/YYYY');
  };

  const handleLanguageChange = (language: string) => {
    i18n.changeLanguage(language);
    setShowLanguageSelect(false);
    console.log(showLanguageSelect)
  };

  const languages = [
    { code: 'en', name: 'English' },
    { code: 'fr', name: 'Français' },
    { code: 'ru', name: 'Русский' }
  ];

  return (
    <IonPage>
      <IonContent style={{ '--background': 'black' }}>
        {/* Profile Header */}
        <div className="flex flex-col items-center mb-8 mt-4">
          <IonAvatar className="w-24 h-24 mb-4">
            <IonIcon icon={personCircle} className='h-full w-full' />
          </IonAvatar>
          <h2 className="text-2xl font-bold text-white mb-1">
            {userData?.firstName} {userData?.lastName}
          </h2>
          <p className="text-[#31D526]">{userData?.email}</p>
        </div>

        {/* Subscription Status */}
        <div className="bg-[#1C1C1E] rounded-xl p-4 mb-6">
          <h3 className="text-white text-xl mb-2">{t('dashboard.subscription')}</h3>
          <div className="flex justify-between items-center">
            <div>
              <p className="text-[#31D526]">
                {userData?.subscriptionStatus === 'active'
                  ? t('dashboard.activeSubscription')
                  : t('dashboard.noSubscription')}
              </p>
              {userData?.subscriptionEndDate && (
                <p className="text-gray-400 text-sm">
                  {t('dashboard.expiresOn')} {formatDate(userData.subscriptionEndDate)}
                </p>
              )}
            </div>
            {userData?.subscriptionStatus !== 'active' && (
              <IonButton
                fill="solid"
                className="bg-[#31D526] text-black rounded-full"
                onClick={() => history.push('/payment')}
              >
                {t('dashboard.subscribe')}
              </IonButton>
            )}
          </div>
        </div>

        {/* Settings List */}
        <IonList className="bg-transparent pb-20">
          <IonItem lines="full" className="bg-[#1C1C1E] mb-2 rounded-lg" onClick={() => history.push('/payment')}>
            <IonIcon icon={cardOutline} slot="start" className="text-[#31D526]" />
            <IonLabel className="text-white">{t('dashboard.subscription')}</IonLabel>
          </IonItem>

          <IonItem lines="full" className="bg-[#1C1C1E] mb-2 rounded-lg">
            <IonIcon icon={languageOutline} slot="start" className="text-[#31D526]" />
            <IonLabel className="text-white">{t('dashboard.language')}</IonLabel>
            <IonSelect
              interface="action-sheet"
              value={i18n.language}
              onIonChange={(e) => handleLanguageChange(e.detail.value)}
              className="text-white"
            >
              {languages.map((lang) => (
                <IonSelectOption key={lang.code} value={lang.code}>
                  {lang.name}
                </IonSelectOption>
              ))}
            </IonSelect>
          </IonItem>

          <IonItem lines="full" className="bg-[#1C1C1E] mb-2 rounded-lg" onClick={handleLogout}>
            <IonIcon icon={logOutOutline} slot="start" className="text-[#31D526]" />
            <IonLabel className="text-white">{t('dashboard.logout')}</IonLabel>
          </IonItem>
        </IonList>

        <NavBar />
      </IonContent>
    </IonPage>
  );
};

export default Dashboard;
